import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Coins, CreditCard, Bitcoin, Smartphone, Check, Sparkles } from "lucide-react";

const METHOD_META = {
  crypto: { label: "Krypto", sub: "BTC / ETH / USDT — best pris", icon: Bitcoin, color: "text-orange-400", border: "border-orange-400/40" },
  card: { label: "Betalingskort", sub: "Visa / Mastercard — verifiserer 18+", icon: CreditCard, color: "text-blue-400", border: "border-blue-400/40" },
  strex: { label: "Mobiltelefon", sub: "Strex SMS — verifiserer 18+", icon: Smartphone, color: "text-[var(--magenta)]", border: "border-[var(--magenta)]/40" },
};

export default function Kjop() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("crypto");
  const [tokens, setTokens] = useState(500);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/wallet/packages").then(r => setData(r.data));
  }, []);

  const initPurchase = async () => {
    setBusy(true);
    try {
      if (method === "crypto") {
        // Ekte NOWPayments-flow
        const r = await api.post("/payments/nowpayments/create", { tokens });
        if (r.data.invoice_url) {
          // Lagre purchase_id i sessionStorage så status-siden vet hva som ble startet
          sessionStorage.setItem("last_crypto_purchase_id", r.data.purchase_id);
          // Redirect til NOWPayments sin hostede checkout
          window.location.href = r.data.invoice_url;
        } else {
          toast.error("Kunne ikke opprette krypto-faktura.");
        }
      } else {
        // Mock-flow for kort/Strex (kobles på ekte API senere)
        const r = await api.post("/wallet/purchase/init", { tokens, method });
        navigate(r.data.redirect_url);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Sjekk om Strex-bruker har valgt en pakke som ikke støttes
  useEffect(() => {
    if (!data) return;
    const avail = data.packages.filter(p => p.methods[method]);
    if (!avail.find(p => p.tokens === tokens)) {
      setTokens(avail[0]?.tokens || 100);
    }
  }, [method, data]); // eslint-disable-line

  if (!data) return <Layout><div className="font-meta text-[var(--cream-soft)]">Laster…</div></Layout>;

  // Filtrer pakker basert på metode
  const availablePackages = data.packages.filter(p => p.methods[method]);
  const selectedPkg = availablePackages.find(p => p.tokens === tokens) || availablePackages[0];
  const selectedPrice = selectedPkg?.methods[method];

  return (
    <Layout withSidebar={false}>
      <div className="max-w-5xl mx-auto">
        <div className="font-meta mb-3">Wallet</div>
        <h1 className="font-fraunces italic text-5xl text-[var(--cream)] mb-10 leading-tight">Kjøp tokens.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* 01: BETALINGSMETODE */}
          <div data-testid="step-method">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-meta text-[var(--magenta)]">01</span>
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Velg betalingsmåte</h2>
            </div>
            <div className="space-y-3">
              {data.providers.map((p) => {
                const m = METHOD_META[p.key];
                const Icon = m.icon;
                const selected = method === p.key;
                return (
                  <button key={p.key} onClick={() => setMethod(p.key)}
                    data-testid={`method-${p.key}`}
                    className={`w-full text-left p-5 border ${selected ? `${m.border} bg-white/[0.04]` : "border-white/10 hover:border-white/30"} transition-all`}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <Icon className={`h-5 w-5 ${m.color}`} />
                        <div>
                          <div className="font-fraunces italic text-lg text-[var(--cream)]">{m.label}</div>
                          <div className="font-meta text-[var(--cream-soft)]">{m.sub}</div>
                        </div>
                      </div>
                      {selected && <Check className="h-4 w-4 text-emerald-400" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {method !== "crypto" && (
              <div className="mt-4 border-l-2 border-[var(--gold)]/40 bg-[var(--gold)]/5 p-3" data-testid="verify-bonus-banner">
                <div className="font-meta text-[var(--gold)] mb-1 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> VERIFISERINGS-BONUS</div>
                <p className="font-manrope text-xs text-[var(--cream-soft)]">
                  Ditt første kjøp via {METHOD_META[method].label.toLowerCase()} gir <strong className="text-[var(--gold)]">+{data.signup_bonus} bonus-tokens</strong> og «✓ Verifisert»-merke.
                </p>
              </div>
            )}
          </div>

          {/* 02: PAKKE */}
          <div data-testid="step-package">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="font-meta text-[var(--magenta)]">02</span>
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Velg token-pakke</h2>
            </div>
            <div className="space-y-2 mb-6">
              {availablePackages.map((p) => {
                const price = p.methods[method];
                const selected = tokens === p.tokens;
                const savings = price.list_price - price.final_price;
                return (
                  <button key={p.tokens} onClick={() => setTokens(p.tokens)}
                    data-testid={`pkg-${p.tokens}-${method}`}
                    className={`w-full p-4 border text-left transition-all flex items-center justify-between gap-4 ${
                      selected ? "border-[var(--magenta)] bg-[var(--magenta)]/5" : "border-white/10 hover:border-white/30"
                    }`}>
                    <div>
                      <div className="font-fraunces italic text-xl text-[var(--cream)] inline-flex items-baseline gap-2">
                        {price.final_price} <span className="font-meta text-[var(--cream-soft)]">NOK</span>
                      </div>
                      {savings > 0 && (
                        <div className="font-meta text-emerald-400 mt-0.5">
                          Spar {savings} kr ({price.discount_pct}% rabatt)
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-fraunces italic text-lg text-[var(--gold)] inline-flex items-center gap-1.5">
                        <Coins className="h-4 w-4" /> {p.tokens}
                      </div>
                      <div className="font-meta text-[var(--cream-faint)]">{price.price_per_token} kr/token</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedPrice && (
              <div className="border-t border-white/10 pt-5 mb-5">
                <div className="flex justify-between items-baseline font-manrope text-[var(--cream)] mb-2">
                  <span>Du betaler</span>
                  <span className="font-fraunces italic text-2xl">{selectedPrice.final_price} NOK</span>
                </div>
                <div className="flex justify-between items-baseline font-manrope text-sm text-[var(--cream-soft)]">
                  <span>Du får</span>
                  <span className="text-[var(--gold)] inline-flex items-center gap-1"><Coins className="h-3.5 w-3.5" /> {tokens} tokens</span>
                </div>
              </div>
            )}

            <button onClick={initPurchase} disabled={busy || !selectedPrice}
              className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-3"
              data-testid="checkout-btn">
              {busy ? "Setter opp betaling…" : `Fortsett til ${METHOD_META[method].label}`}
            </button>
            <p className="font-meta text-[var(--cream-faint)] mt-3 text-center">
              I testperioden er all betaling MOCKAD – ingen ekte penger trekkes.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
